// Stridsmelding — Forsvarets standardformat for meldinger i strid/krig.
// Felt: DTG · FRA · TIL · HVEM · HVA · HVOR · NÅR · HVORLEDES.
// Regler: kort og konsist, uten rom for misforståelser. HIMMELRETNINGER,
// STEDSNAVN, KODENAVN og ordet «IKKE» skal skrives med STORE bokstaver.
// DTG = dato-tids-gruppe (ddhhmm + tidssonebokstav + MND + ÅÅ).
// Tidssoner innenlands: ZULU (Z = GMT), ALFA (A = norsk normaltid, UTC+1),
// BRAVO (B = norsk sommertid, UTC+2).
import { z } from 'zod';
export const StridsSone = z.enum(['Z', 'A', 'B']);
export const SONE_LABEL = { Z: 'ZULU', A: 'ALFA', B: 'BRAVO' };
const SONE_OFFSET_H = { Z: 0, A: 1, B: 2 };
const MND = ['JAN', 'FEB', 'MAR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DES'];
export const StridsmeldingSchema = z.object({
    id: z.string().min(1).max(64),
    dtgMs: z.number(),
    sone: StridsSone.default('A'),
    fra: z.string().max(120),
    til: z.string().max(120),
    hvem: z.string().max(400),
    hva: z.string().max(600),
    hvor: z.string().max(300),
    naar: z.string().max(120),
    hvorledes: z.string().max(1500),
    /** Valgfri posisjon (for kart). */
    lat: z.number().optional(),
    lon: z.number().optional(),
    createdBy: z.string().max(128).optional(),
    tsMs: z.number(),
});
const p2 = (n) => String(n).padStart(2, '0');
/** Bygg DTG: ddhhmm + sonebokstav + MND + ÅÅ (f.eks. «201430A JUL 26»). */
export function buildDtg(ms, sone) {
    const d = new Date(ms + SONE_OFFSET_H[sone] * 3_600_000);
    const dd = p2(d.getUTCDate());
    const hh = p2(d.getUTCHours());
    const mm = p2(d.getUTCMinutes());
    const mnd = MND[d.getUTCMonth()];
    const yy = p2(d.getUTCFullYear() % 100);
    return `${dd}${hh}${mm}${sone} ${mnd} ${yy}`;
}
// Himmelretninger som skal STORT-skrives (inkl. sammensatte + forkortelser).
const RETNINGER = [
    'nordnordøst', 'østnordøst', 'østsørøst', 'sørsørøst', 'sørsørvest', 'vestsørvest', 'vestnordvest', 'nordnordvest',
    'nordøst', 'nordvest', 'sørøst', 'sørvest', 'sydøst', 'sydvest',
    'nord', 'sør', 'syd', 'øst', 'vest',
];
const RETNING_RE = new RegExp(`(?<![\\p{L}])(${RETNINGER.join('|')}|ikke)(?![\\p{L}])`, 'giu');
/** Doktrine: HIMMELRETNINGER og ordet «IKKE» skrives med STORE bokstaver.
 * (Stedsnavn/kodenavn må operatøren selv skrive med versaler — kan ikke utledes.) */
export function stridsEmphasis(text) {
    return text.replace(RETNING_RE, (m) => m.toUpperCase());
}
/** Formater en stridsmelding som ren tekst (kanonisk 8-linjers oppsett). */
export function formatStridsmelding(m) {
    const e = stridsEmphasis;
    return [
        `DTG: ${buildDtg(m.dtgMs, m.sone)}`,
        `FRA: ${e(m.fra)}`,
        `TIL: ${e(m.til)}`,
        `HVEM: ${e(m.hvem)}`,
        `HVA: ${e(m.hva)}`,
        `HVOR: ${e(m.hvor)}`,
        `NÅR: ${e(m.naar)}`,
        `HVORLEDES: ${e(m.hvorledes)}`,
    ].join('\n');
}
export function parseStridsmelding(raw) {
    const r = StridsmeldingSchema.safeParse(raw);
    return r.success ? r.data : null;
}
//# sourceMappingURL=stridsmelding.js.map